import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PromotionDetailResponse } from '@wconcept/wconcept-common/models/renewal/promotion';
import MegaPromotionTemplate from '@wconcept/wconcept-ui-renewal/src/components/templates/MegaPromotionTemplate';
import { useVisiblePromotionProducts } from '@hooks/useVisiblePromotionProducts';
import { getPromotionVisibleThroughOrder } from '@hooks/useVisibleModules';
import { useRelatedPrdModalRestore } from '@hooks/useModalRestore';
import SuspenseWrapper from '@components/common/SuspenseWrapper';
import SuspenseWithMinimumLoading from '@components/SuspenseWithMinimumLoading';
import { useServerTimeStore } from '@store/serverTimeStore';
import useScrollStore from '@store/scrollStore';
import useGlobalState from '@hooks/useGlobalState';
import {
  renderHtmlModule,
  renderImageVideoModule,
  renderKeyVisualModule,
  renderSpecialPriceModule,
  renderFirstPurchaseModule,
  renderBrandModule,
  renderRankingModule,
  renderPaymentBenefitModule,
  renderShortFormModule,
  renderContentModule,
  renderBasicNavigationModule,
  renderCommonNavigationModule,
  renderCouponModule,
  renderAdvertisementModule,
  renderWLiveModule,
  renderProductModule,
  renderProductListModule,
  renderFcfsCouponModule,
  renderHighlightPickModule,
  renderBrandCollectionModule,
  renderEntryModule,
  renderMainBannerModule,
  renderQuickBannerModule,
  renderTopBuyerModule,
} from './utils/promotionModuleRenderer';
import { isInDisplayTimeRange } from './utils/displayTimeChecker';
import { isHtmlModule } from './utils/typeGuards';
import { isFixedClassChangeNeeded } from './fixedNavigation';

/**
 * 프로모션 모듈 컨테이너 컴포넌트
 * 각 프로모션 모듈을 렌더링하는 컨테이너 컴포넌트입니다.
 */
interface PromotionModulesContainerProps {
  promotionInfo?: PromotionDetailResponse;
  isMobile: boolean;
  nonce?: string;
  gnbType?: boolean;
  templateClassName?: string;
  serverTime?: string;
  productListTotalCount?: number;
  isPaused?: boolean;
  setIsPaused?: (value: boolean) => void;
  setHtmlComplete?: (value: boolean) => void;
  renderMobileShareButton?: () => React.ReactNode;
  initialVisibleThroughOrder?: number;
}

function PromotionModulesContainer({
  promotionInfo,
  isMobile,
  nonce,
  gnbType,
  templateClassName,
  serverTime,
  productListTotalCount,
  isPaused,
  setIsPaused,
  setHtmlComplete,
  renderMobileShareButton,
  initialVisibleThroughOrder,
}: PromotionModulesContainerProps) {
  const { assistData } = useGlobalState();
  const isLogin = !!assistData?.isLogin;
  // 하이드레이션 완료 여부 추적
  const [isHydrated, setIsHydrated] = useState(false);
  const htmlCompleteRef = useRef<boolean>(false);
  // 서버 시간을 렌더링 전에 동기적으로 설정 (하이드레이션 일관성 보장)
  if (serverTime) {
    const currentServerTime = useServerTimeStore.getState().serverTime;
    if (!currentServerTime) {
      useServerTimeStore.setState({ serverTime });
    }
  }

  // 전부 완료된 후에만 완료 처리
  const totalExpectedHtmlWide = useMemo(() => {
    const list = promotionInfo?.contents ?? [];
    return list
      .filter(c => c.moduleType === 'HTML_WIDE')
      .filter(content => {
        const filtered = content.modules
          ?.filter(isHtmlModule)
          ?.filter(
            m =>
              isInDisplayTimeRange(m.displayStartDate, m.displayEndDate) &&
              m.displayStatus === 'USE'
          );
        return (filtered?.length ?? 0) > 0;
      }).length;
  }, [promotionInfo?.contents]);

  const htmlWideKey = useMemo(
    () =>
      (promotionInfo?.contents ?? [])
        .filter(c => c.moduleType === 'HTML_WIDE')
        .map(c => c.contentId)
        .sort()
        .join(','),
    [promotionInfo?.contents]
  );

  const firstVisibleProductListIndex = useMemo(
    () =>
      (promotionInfo?.contents ?? []).findIndex(
        content =>
          content.moduleType === 'PRODUCT_LIST' &&
          content.displayStatus === 'USE' &&
          productListTotalCount !== 0
      ),
    [promotionInfo?.contents, productListTotalCount]
  );

  const firstVisibleShareButtonIndex = useMemo(() => {
    const contents = promotionInfo?.contents ?? [];
    return contents.findIndex(
      content =>
        content.moduleType === 'BRAND_COLLECTION' &&
        content.displayStatus === 'USE'
    );
  }, [promotionInfo?.contents]);

  const completedHtmlCountRef = useRef(0);
  const prevHtmlWideKeyRef = useRef(htmlWideKey);
  if (prevHtmlWideKeyRef.current !== htmlWideKey) {
    prevHtmlWideKeyRef.current = htmlWideKey;
    completedHtmlCountRef.current = 0;
  }

  useEffect(() => {
    if (totalExpectedHtmlWide === 0) {
      setHtmlComplete?.(true);
    }
    // HTML_WIDE가 있으면 각 섹션의 handleHtmlComplete가 호출될 때까지 대기
  }, [totalExpectedHtmlWide, setHtmlComplete]);

  const handleHtmlCompleteMulti = useCallback(() => {
    completedHtmlCountRef.current += 1;
    if (completedHtmlCountRef.current >= totalExpectedHtmlWide) {
      setHtmlComplete?.(true);
    }
  }, [setHtmlComplete, totalExpectedHtmlWide]);

  // 하이드레이션 완료 후에만 실시간 시간 사용
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // 연관상품 모달 상태 복원
  useRelatedPrdModalRestore();

  // React Query로 보이는 영역의 상품 데이터만 가져오기
  const pendingNaviClick = useScrollStore(state => state.pendingNaviClick);
  const visibleThroughOrder = getPromotionVisibleThroughOrder(
    initialVisibleThroughOrder,
    pendingNaviClick
  );
  const {
    moduleProductDataMap,
    // productMap,
    queryStatusMap,
    directQueryStatusMap, // 직접적인 useQuery 상태 (권장)
    registerModule,
    isModuleVisible,
  } = useVisiblePromotionProducts({
    contents: promotionInfo?.contents || [],
    enabled: !!promotionInfo?.contents?.length,
    rootMargin: '1000px', // 1000px 전에 미리 로딩
    threshold: 0.01, // 1%만 보여도 감지
    initialVisibleThroughOrder: visibleThroughOrder,
  });

  // 스크롤/리플로우가 멎은 다음 프레임에 후행 재계산을 거는 RAF 핸들
  const settleRafRef = useRef(0);

  // fixed 스왑 계산 본문. 스크롤 이벤트와 후행 재계산이 공유한다.
  const applyFixedSwap = useCallback(() => {
    const navi = document.querySelector('.corner-navi1') as HTMLDivElement;
    const navi2 = document.querySelector('.corner-navi2') as HTMLDivElement;
    const collectionNavi = document.querySelector(
      '.corner-collection-section'
    ) as HTMLDivElement;
    const lpNavi = document.querySelector('.corner-lp-wrap') as HTMLDivElement;

    // 우선순위에 따른 navi 요소 선택
    const targetNavi = navi && navi2 ? navi : navi2 || navi;

    const lpNaviTopPos =
      (lpNavi?.getBoundingClientRect()?.top || 0) + window.pageYOffset;
    const scrollY = -document.body.getBoundingClientRect().top;
    const headerHght =
      (
        document.querySelector('#header') ||
        document.querySelector('#headerDesktop .inner-header')
      )?.clientHeight || 0;
    const mainGnbHght =
      document.querySelector('#main-gnb-bar')?.clientHeight || 0;
    // sub-gnb-list 높이 (scrollUp 시 노출되어 전체 고정 높이에 포함)
    const subGnbHght =
      document.querySelector('.sub-gnb-list')?.clientHeight || 0;

    // 고정 내비가 붙는 top 오프셋. MainLayout이 부여하는 is-scrollUp/is-fix-header를 읽어
    // CSS 표시 위치와 일치시킨다. scrollDown은 main-gnb만, scrollUp은 header+gnb+sub-gnb 노출.
    const page = document.querySelector('.mega-promotion-page');
    const isScrollUp =
      !!page?.classList.contains('is-fix-header') &&
      !!page?.classList.contains('is-scrollUp');

    const getFixedOffset = () => {
      if (isScrollUp) {
        return mainGnbHght + headerHght + subGnbHght;
      }
      if (gnbType) {
        return mainGnbHght;
      }
      return headerHght;
    };

    const fixedOffset = getFixedOffset();

    // corner-lp-wrap은 fixed 시 padding-top이 collapse되므로, 그만큼 보정해
    // 고정/해제 시점을 wrapper가 아닌 컨텐츠 기준으로 맞춘다.
    const lpPadTop = lpNavi
      ? parseFloat(getComputedStyle(lpNavi).paddingTop) || 0
      : 0;
    const lpPoint = lpNaviTopPos + lpPadTop - fixedOffset;

    // LP 영역 도달 시 고정
    const isLpFixed = lpPoint < scrollY;
    if (
      lpNavi &&
      isFixedClassChangeNeeded(lpNavi.classList.contains('fixed'), isLpFixed)
    ) {
      lpNavi.classList.toggle('fixed', isLpFixed);
    }

    // 브랜드 모아보기 category-tab fixed 처리
    const collectionTab = collectionNavi?.querySelector(
      '.category-tab'
    ) as HTMLDivElement;
    const collectionTabTopPos = collectionTab
      ? collectionTab.getBoundingClientRect().top + window.pageYOffset
      : 0;
    const collectionPoint = collectionTabTopPos - fixedOffset;

    if (collectionNavi) {
      // 더 아래 LP가 고정되기 직전(LP 기준점 - tab 높이)부터 미리 양보해,
      // collection 해제와 LP 고정이 동시에 swap되지 않게 한다.
      const isCollectionFixed =
        lpNavi && lpPoint - (collectionTab?.clientHeight || 0) < scrollY
          ? false
          : collectionPoint < scrollY;
      if (
        isFixedClassChangeNeeded(
          collectionNavi.classList.contains('fixed'),
          isCollectionFixed
        )
      ) {
        collectionNavi.classList.toggle('fixed', isCollectionFixed);
      }
    }

    // Navi fixed 처리
    if (targetNavi) {
      const naviTopPos =
        targetNavi.getBoundingClientRect().top + window.pageYOffset;
      const naviPoint = naviTopPos - fixedOffset;

      if (naviPoint < scrollY) {
        // lp/collection이 고정 구간(고정 위치 - navi 높이 버퍼)에 들어오면 navi는 미리 양보해,
        // 두 내비가 동시에 fixed로 겹치는 것을 막는다.
        const shouldBeFixed = !(
          (lpNavi && lpPoint - targetNavi.clientHeight < scrollY) ||
          (collectionNavi &&
            collectionPoint - targetNavi.clientHeight < scrollY)
        );
        if (
          isFixedClassChangeNeeded(
            targetNavi.classList.contains('fixed'),
            shouldBeFixed
          )
        ) {
          targetNavi.classList.toggle('fixed', shouldBeFixed);
        }
      } else if (
        isFixedClassChangeNeeded(targetNavi.classList.contains('fixed'), false)
      ) {
        targetNavi.classList.remove('fixed');
      }
    }
  }, [gnbType]);

  const handleScroll = useCallback(() => {
    // 1) 현재 스크롤 위치 기준 즉시 계산
    applyFixedSwap();

    // 2) 카테고리 탭 변경 등으로 리플로우가 일어나면 브라우저 스크롤 앵커링으로 scrollY가
    //    튄 채 fixed 스왑이 잘못 굳을 수 있다(collection 해제 + corner-navi1이 덮음). 스크롤/
    //    리플로우가 멎은 다음 프레임에 한 번 더 계산해 안정된 DOM 기준으로 바로잡는다.
    cancelAnimationFrame(settleRafRef.current);
    settleRafRef.current = requestAnimationFrame(() => {
      settleRafRef.current = requestAnimationFrame(applyFixedSwap);
    });
  }, [applyFixedSwap]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(settleRafRef.current);
    };
  }, [handleScroll]);

  if (!promotionInfo?.contents?.length) {
    return null;
  }

  return (
    <MegaPromotionTemplate className={templateClassName}>
      <SuspenseWrapper id="PROMOTION_MODULES">
        <div suppressHydrationWarning>
          {promotionInfo.contents.map((content, index) => {
            switch (content.moduleType) {
              case 'HTML_WIDE':
                return renderHtmlModule(
                  content,
                  isMobile,
                  nonce,
                  registerModule,
                  handleHtmlCompleteMulti
                );

              case 'IMAGE_VIDEO':
                return renderImageVideoModule(
                  content,
                  isMobile,
                  registerModule,
                  promotionInfo.promotion
                );

              case 'KEY_VISUAL':
                return renderKeyVisualModule(content, isMobile, registerModule);

              case 'MAIN_BANNER':
                return renderMainBannerModule(
                  content,
                  isMobile,
                  registerModule
                );

              case 'QUICK_BANNER':
                return renderQuickBannerModule(
                  content,
                  isMobile,
                  registerModule
                );

              case 'SPECIAL_PRICE':
                return renderSpecialPriceModule({
                  content,
                  isMobile,
                  registerModule,
                  isModuleVisible,
                  promotion: promotionInfo.promotion,
                });

              case 'FIRST_PURCHASE':
                return renderFirstPurchaseModule({
                  content,
                  isMobile,
                  registerModule,
                  isModuleVisible,
                  promotion: promotionInfo.promotion,
                });

              case 'BRAND':
                return renderBrandModule(
                  content,
                  isMobile,
                  promotionInfo?.promotion?.title,
                  registerModule,
                  isModuleVisible
                );

              case 'RANKING':
                return renderRankingModule(content, isMobile, registerModule);

              case 'PAYMENT_BENEFIT':
                return renderPaymentBenefitModule(
                  content,
                  isMobile,
                  registerModule
                );

              case 'SHORT_FORM':
                return renderShortFormModule({
                  content,
                  isMobile,
                  registerModule,
                  isModuleVisible,
                  queryStatusMap,
                  directQueryStatusMap, // 직접적인 useQuery 상태 추가
                  isPaused,
                  setIsPaused,
                  promotionTitle: promotionInfo.promotion?.title,
                });

              case 'CONTENT':
                return renderContentModule(content, isMobile, registerModule);

              case 'BASIC_NAVIGATION':
                return renderBasicNavigationModule(
                  content,
                  isMobile,
                  promotionInfo.contents,
                  registerModule
                );

              case 'COMMON_NAVIGATION':
                return renderCommonNavigationModule(
                  content,
                  isMobile,
                  promotionInfo.promotion?.title,
                  registerModule
                );

              case 'COUPON':
                return renderCouponModule(content, isMobile, registerModule);

              case 'ADVERTISEMENT':
                return renderAdvertisementModule(
                  content,
                  isMobile,
                  promotionInfo.promotion,
                  registerModule
                );

              case 'W_LIVE':
                return renderWLiveModule(content, isMobile, registerModule);

              case 'PRODUCT':
                return renderProductModule(
                  content,
                  isMobile,
                  promotionInfo?.promotion?.title,
                  registerModule,
                  isModuleVisible
                );

              case 'PRODUCT_LIST':
                return (
                  <React.Fragment key={content.contentId}>
                    {renderProductListModule(
                      content,
                      isMobile,
                      registerModule,
                      productListTotalCount
                    )}
                  </React.Fragment>
                );
              case 'FCFS_COUPON':
                return renderFcfsCouponModule(
                  content,
                  isMobile,
                  registerModule,
                  promotionInfo.promotion?.title,
                  promotionInfo.promotion ?? null
                );

              case 'HIGHLIGHT_PICK':
                return renderHighlightPickModule({
                  content,
                  isMobile,
                  registerModule,
                  isModuleVisible,
                  promotion: promotionInfo.promotion,
                });

              case 'BRAND_COLLECTION':
                return (
                  <React.Fragment key={content.contentId}>
                    {renderBrandCollectionModule(
                      content,
                      isMobile,
                      registerModule,
                      gnbType
                    )}
                  </React.Fragment>
                );
              case 'ENTRY':
                return renderEntryModule({
                  content,
                  isMobile,
                  registerModule,
                  promotionTitle: promotionInfo.promotion?.title,
                });

              case 'TOP_BUYER':
                return renderTopBuyerModule({
                  content,
                  isMobile,
                  registerModule,
                  isLogin,
                });

              default:
                console.warn(`지원되지 않는 모듈 타입: ${content.moduleType}`);
                return null;
            }
          })}
        </div>
      </SuspenseWrapper>
    </MegaPromotionTemplate>
  );
}

export default memo(PromotionModulesContainer);
